import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaWindowClose, FaSearch, FaTimes } from 'react-icons/fa';
import CommonModal from './CommonModal';
import {
  catchError,
  commonModalSetting,
  enterFn,
  formatWon,
  maskingInfo,
  onlyDigits,
} from 'JS/common';
import { createAdminOrder, searchSubscriptionUser } from 'JS/API';

const AdminOrderIssueModal = ({ onClose, onIssued }) => {
  const [selectedUser, setSelectedUser] = useState(null);

  // 회원 검색 (인라인)
  const [searchTarget, setSearchTarget] = useState('name');
  const [searchTxt, setSearchTxt] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const [merchantName, setMerchantName] = useState('');
  const [supplyPrice, setSupplyPrice] = useState('');
  const [vat, setVat] = useState('');
  const [vatTouched, setVatTouched] = useState(false);
  const [expireDays, setExpireDays] = useState(7);
  const [memo, setMemo] = useState('');

  const [loading, setLoading] = useState(false);

  const [innerAlertBox, setInnerAlertBox] = useState({
    mode: '',
    context: '',
    bool: false,
  });
  const [innerAlert, setInnerAlert] = useState('');
  const navigate = useNavigate();

  const handleBackgroundClick = e => {
    if (e.target === e.currentTarget) {
      e.stopPropagation();
      onClose();
    }
  };

  // 회원 검색
  const handleSearch = async () => {
    if (!searchTxt.trim()) {
      return commonModalSetting(
        setInnerAlertBox,
        true,
        'alert',
        '검색어를 입력해 주세요.'
      );
    }
    setSearching(true);
    const result = await searchSubscriptionUser(
      searchTarget,
      searchTxt.trim(),
      1,
      20
    );
    setSearching(false);
    if (typeof result === 'object') {
      const users = result?.data?.data || [];
      setSearchResults(users);
      if (users.length === 0) {
        commonModalSetting(
          setInnerAlertBox,
          true,
          'alert',
          '검색 결과가 없습니다.'
        );
      }
    } else {
      catchError(result, navigate, setInnerAlertBox, setInnerAlert);
    }
  };

  const handleSelectUser = user => {
    setSelectedUser(user);
    setSearchResults([]);
    setSearchTxt('');
  };

  const handleRemoveUser = () => {
    setSelectedUser(null);
  };

  // 공급가 변경 → vat 자동 계산 (수동 수정 전까지)
  useEffect(() => {
    if (!vatTouched) {
      const sp = Number(supplyPrice) || 0;
      setVat(sp > 0 ? Math.round(sp * 0.1) : '');
    }
  }, [supplyPrice, vatTouched]);

  const total = useMemo(() => {
    return (Number(supplyPrice) || 0) + (Number(vat) || 0);
  }, [supplyPrice, vat]);

  const handleSubmit = async () => {
    if (!selectedUser) {
      return commonModalSetting(
        setInnerAlertBox,
        true,
        'alert',
        '회원을 선택해 주세요.'
      );
    }
    if (!merchantName.trim() || merchantName.trim().length > 255) {
      return commonModalSetting(
        setInnerAlertBox,
        true,
        'alert',
        '항목명을 1~255자로 입력해 주세요.'
      );
    }
    const sp = Number(supplyPrice);
    if (!sp || sp <= 0) {
      return commonModalSetting(
        setInnerAlertBox,
        true,
        'alert',
        '공급가를 1원 이상 입력해 주세요.'
      );
    }
    const vatNum = Number(vat) || 0;
    if (vatNum < 0) {
      return commonModalSetting(
        setInnerAlertBox,
        true,
        'alert',
        '부가세는 0원 이상이어야 합니다.'
      );
    }
    const days = Number(expireDays);
    if (!days || days < 1 || days > 90) {
      return commonModalSetting(
        setInnerAlertBox,
        true,
        'alert',
        '결제 가능 기한은 1~90일 사이여야 합니다.'
      );
    }
    if (memo.length > 1000) {
      return commonModalSetting(
        setInnerAlertBox,
        true,
        'alert',
        '메모는 최대 1000자까지 입력 가능합니다.'
      );
    }

    const userPk = selectedUser.user_pk || selectedUser.id;
    const body = {
      user_pk: userPk,
      merchant_name: merchantName.trim(),
      supply_price: sp,
      vat: vatNum,
      expire_days: days,
    };
    if (memo.trim()) body.memo = memo.trim();

    setLoading(true);
    const result = await createAdminOrder(body);
    setLoading(false);

    if (typeof result === 'object') {
      const created = result?.data?.data;
      onIssued(created);
    } else {
      catchError(result, navigate, setInnerAlertBox, setInnerAlert);
    }
  };

  return (
    <>
      <div className='modal-background' onClick={handleBackgroundClick}>
        <div className='modal admin-order-issue'>
          <div className='topBar'>
            <h2>결제건 발행</h2>
            <div onClick={onClose}>
              <FaWindowClose />
            </div>
          </div>
          <hr />
          <div className='wrap'>
            {/* 회원 검색 */}
            <div className='search-section'>
              <label>회원 검색 *</label>
              <div className='search-row'>
                <select
                  value={searchTarget}
                  onChange={e => setSearchTarget(e.target.value)}>
                  <option value='name'>이름</option>
                  <option value='email'>이메일</option>
                  <option value='id'>아이디</option>
                </select>
                <input
                  type='text'
                  value={searchTxt}
                  onChange={e => setSearchTxt(e.target.value)}
                  onKeyDown={e => enterFn(e, handleSearch)}
                  placeholder='검색어 입력'
                />
                <button
                  className='search-btn'
                  onClick={handleSearch}
                  disabled={searching}>
                  <FaSearch />
                </button>
              </div>

              {searchResults.length > 0 && (
                <div className='search-results'>
                  {searchResults.map(user => {
                    const userPk = user.user_pk || user.id;
                    return (
                      <div
                        key={userPk}
                        className='search-result-item'
                        onClick={() => handleSelectUser(user)}>
                        <span className='name'>
                          {user.user_name || user.name
                            ? maskingInfo('name', user.user_name || user.name)
                            : '-'}
                        </span>
                        <span className='email'>
                          {user.user_email || user.email
                            ? maskingInfo('email', user.user_email || user.email)
                            : '-'}
                        </span>
                        <span className='id'>
                          {user.user_id ? maskingInfo('id', user.user_id) : '-'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* 선택된 회원 */}
            <div className='selected-section'>
              <label>선택된 회원</label>
              <div className='selected-users'>
                {!selectedUser ? (
                  <div className='empty'>선택된 회원이 없습니다.</div>
                ) : (
                  <div className='selected-user-item'>
                    <span className='name'>
                      {maskingInfo(
                        'name',
                        selectedUser.user_name || selectedUser.name || ''
                      )}
                    </span>
                    <span className='email'>
                      {selectedUser.user_email || selectedUser.email
                        ? maskingInfo(
                            'email',
                            selectedUser.user_email || selectedUser.email
                          )
                        : '-'}
                    </span>
                    <button className='remove-btn' onClick={handleRemoveUser}>
                      <FaTimes />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* 항목명 */}
            <div className='input-section'>
              <label>항목명 *</label>
              <input
                type='text'
                value={merchantName}
                onChange={e => setMerchantName(e.target.value.slice(0, 255))}
                placeholder='예: 결제 요청'
                maxLength={255}
              />
              <p className='hint'>{merchantName.length} / 255자</p>
            </div>

            {/* 공급가 */}
            <div className='input-section'>
              <label>공급가 *</label>
              <div className='input-row'>
                <input
                  type='text'
                  inputMode='numeric'
                  value={
                    supplyPrice === ''
                      ? ''
                      : Number(supplyPrice).toLocaleString('ko-KR')
                  }
                  onChange={e => setSupplyPrice(onlyDigits(e.target.value))}
                  placeholder='0'
                />
                <span className='suffix'>원</span>
              </div>
            </div>

            {/* 부가세 */}
            <div className='input-section'>
              <label>부가세</label>
              <div className='input-row'>
                <input
                  type='text'
                  inputMode='numeric'
                  value={
                    vat === '' ? '' : Number(vat).toLocaleString('ko-KR')
                  }
                  onChange={e => {
                    setVatTouched(true);
                    setVat(onlyDigits(e.target.value));
                  }}
                  placeholder='0'
                />
                <span className='suffix'>원</span>
                {vatTouched && (
                  <button
                    className='cancel-btn small'
                    onClick={() => setVatTouched(false)}>
                    자동 계산
                  </button>
                )}
              </div>
              <p className='hint'>
                자동: 공급가의 10% 반올림 (필요 시 직접 수정 가능)
              </p>
            </div>

            {/* 총액 */}
            <div className='preview-section'>
              <div className='preview-row'>
                <span className='label'>총액</span>
                <span className='value highlight'>{formatWon(total)}</span>
              </div>
            </div>

            {/* 결제 가능 기한 */}
            <div className='input-section'>
              <label>결제 가능 기한</label>
              <div className='input-row'>
                <input
                  type='number'
                  min='1'
                  max='90'
                  value={expireDays}
                  onChange={e => setExpireDays(e.target.value)}
                />
                <span className='suffix'>일</span>
              </div>
              <p className='hint'>1~90일 사이 (기본 7일)</p>
            </div>

            {/* 메모 */}
            <div className='input-section'>
              <label>메모</label>
              <textarea
                rows={3}
                value={memo}
                onChange={e => setMemo(e.target.value.slice(0, 1000))}
                placeholder='어드민 메모 (선택)'
              />
              <p className='hint'>{memo.length} / 1000자</p>
            </div>

            <div className='footer'>
              <div className='btn-group'>
                <button
                  className='confirm-btn'
                  onClick={handleSubmit}
                  disabled={loading}>
                  {loading ? '발행 중...' : '발행하기'}
                </button>
                <button className='cancel-btn' onClick={onClose}>
                  취소
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {innerAlertBox.bool && (
        <CommonModal
          setModal={setInnerAlertBox}
          modal={innerAlertBox}
          okFn={() => {
            if (innerAlert === 'logout') navigate('/');
          }}
        />
      )}
    </>
  );
};

export default AdminOrderIssueModal;
